<script setup lang="ts">
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { IPosDevice } from '@/interfaces/IPosDevice';
  import { ref } from 'vue';
  import { AlertType } from '@/interfaces/IAlert';
  import { copyTextToClipboard } from '@/utils/clipboard.utils';

  const props = defineProps<{
    posDevice: IPosDevice;
  }>();

  const ui = useUi();
  const modals = useModal();

  async function copyToken() {
    try {
      await copyTextToClipboard(props.posDevice.token);
      ui.showToast(AlertType.Success, 'Token copiado al portapapeles');
    } catch (e) {
      ui.showToast(AlertType.Error, 'No se pudo copiar el token');
    }
  }

  const close = () => {
    modals.hide('pos-device-token');
  };
</script>

<template>
  <div class="modals-pos-device-token-container">
    <v-card>
      <v-card-title>
        <span>Token del Dispositivo</span>
        <v-spacer />
        <v-btn icon small @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <div class="token-display">
          <div class="device-name">
            <v-icon left color="primary">mdi-tablet</v-icon>
            <span class="tw-font-medium">{{ posDevice.name }}</span>
          </div>

          <div class="token-section">
            <p class="tw-text-sm tw-text-gray-600 tw-mb-3">
              Use este token para autenticar el dispositivo en el sistema POS:
            </p>

            <div class="token-box">
              <code class="token-text">{{ posDevice.token }}</code>
            </div>

            <v-btn block color="primary" @click="copyToken" class="tw-mt-3">
              <v-icon left>mdi-content-copy</v-icon>
              Copiar Token
            </v-btn>
          </div>

          <v-alert type="warning" dense outlined class="tw-mt-4">
            <div class="tw-text-xs">
              <strong>Importante:</strong> Guarde este token de forma segura.
              Cualquier persona con acceso a este token podrá autenticar
              dispositivos en su sistema POS.
            </div>
          </v-alert>

          <div v-if="!posDevice.deviceId" class="tw-mt-4">
            <v-alert type="info" dense outlined>
              <div class="tw-text-xs">
                Este dispositivo aún no está vinculado. Una vez que se
                autentique por primera vez, quedará vinculado a ese dispositivo
                específico.
              </div>
            </v-alert>
          </div>
          <div v-else class="tw-mt-4">
            <v-alert type="info" dense outlined>
              <div class="tw-text-xs">
                Este dispositivo está vinculado. La desvinculación solo puede
                realizarse desde el propio dispositivo POS.
              </div>
            </v-alert>
          </div>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn color="primary" @click="close">Cerrar</v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<style scoped lang="scss">
  .modals-pos-device-token-container {
    .token-display {
      .device-name {
        @apply tw-flex tw-items-center tw-mb-4 tw-text-lg;
      }

      .token-section {
        @apply tw-bg-gray-50 tw-p-4 tw-rounded-lg;

        .token-box {
          @apply tw-bg-white tw-border-2 tw-border-dashed tw-border-gray-300 tw-rounded tw-p-4 tw-text-center;

          .token-text {
            @apply tw-text-xl tw-font-mono tw-font-bold tw-text-blue-600 tw-break-all;
          }
        }
      }
    }
  }
</style>
